// Roving tabindex shared by the marketplace's `SegmentedControl` and `OptionList` and the
// outreach `TermChipRow`, so an ARIA radiogroup costs one tab stop in both flows rather than
// one per option.

import type { JSX } from 'preact';

/** Arrow-key move, wrapping at both ends. Pairs with
 *  `tabIndex={index === rovingIndex ? 0 : -1}`, and assumes the options are direct
 *  children of the group element — which is how every caller renders. */
export function handleRovingKeyDown<T>(
  event: JSX.TargetedKeyboardEvent<HTMLElement>,
  values: readonly T[],
  index: number,
  onChange: (value: T) => void,
): void {
  const delta =
    event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
  if (delta === 0 || values.length === 0) return;
  event.preventDefault();

  const nextIndex = (index + delta + values.length) % values.length;
  const next = values[nextIndex];
  if (next === undefined) return;
  onChange(next);

  const sibling = event.currentTarget.parentElement?.children[nextIndex];
  if (sibling instanceof HTMLElement) sibling.focus();
}

/** The one option that carries `tabIndex={0}`. Falls back to the first so a group with
 *  no matching value stays reachable. */
export function rovingIndexFor<T>(values: readonly T[], selected: T): number {
  const found = values.indexOf(selected);
  return found === -1 ? 0 : found;
}
